import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SITE } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  event_date: "",
  event_type: "",
  subject: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila i campi obbligatori (nome, email, messaggio).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Richiesta inviata. Ti contatteremo entro 24h.");
      setForm(initial);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      className="relative py-28 md:py-40 bg-[var(--pw-surface)] overflow-hidden"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-16">
        {/* Left info */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="gold-line" />
            <span className="overline">Contatti</span>
          </div>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[var(--pw-text)]">
            Parliamo del<br />
            <em className="text-[var(--pw-gold)]">vostro giorno</em>.
          </h2>
          <p className="mt-8 text-[var(--pw-text)]/70 font-light leading-relaxed max-w-md">
            Raccontaci la cerimonia che immagini: location, data, atmosfera.
            Ti rispondiamo entro 24 ore con un preventivo dettagliato e gratuito.
          </p>

          <div className="mt-12 space-y-7">
            <a
              href={`tel:${SITE.phone2.replace(/\./g, "")}`}
              className="flex items-start gap-5 group"
              data-testid="contact-phone"
            >
              <div className="w-11 h-11 border border-[var(--pw-gold)]/40 flex items-center justify-center text-[var(--pw-gold)] group-hover:bg-[var(--pw-gold)] group-hover:text-black transition-colors duration-500">
                <Phone size={16} />
              </div>
              <div>
                <div className="overline mb-1">Telefono</div>
                <div className="text-[var(--pw-text)] font-light">{SITE.phone1}</div>
                <div className="text-[var(--pw-text)] font-light">{SITE.phone2}</div>
              </div>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              className="flex items-start gap-5 group"
              data-testid="contact-email"
            >
              <div className="w-11 h-11 border border-[var(--pw-gold)]/40 flex items-center justify-center text-[var(--pw-gold)] group-hover:bg-[var(--pw-gold)] group-hover:text-black transition-colors duration-500">
                <Mail size={16} />
              </div>
              <div>
                <div className="overline mb-1">Email</div>
                <div className="text-[var(--pw-text)] font-light break-all">{SITE.email}</div>
              </div>
            </a>

            <div className="flex items-start gap-5">
              <div className="w-11 h-11 border border-[var(--pw-gold)]/40 flex items-center justify-center text-[var(--pw-gold)]">
                <MapPin size={16} />
              </div>
              <div>
                <div className="overline mb-1">Sede</div>
                <div className="text-[var(--pw-text)] font-light max-w-xs leading-relaxed">
                  {SITE.address}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 card-pw p-8 lg:p-12"
          data-testid="contact-form"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-2">Nome e Cognome *</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                required
                className="input-pw"
                placeholder="Mario Rossi"
                data-testid="form-name"
              />
            </div>
            <div>
              <label className="overline block mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                required
                className="input-pw"
                placeholder="mario@esempio.it"
                data-testid="form-email"
              />
            </div>
            <div>
              <label className="overline block mb-2">Telefono</label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="input-pw"
                placeholder="+39 ..."
                data-testid="form-phone"
              />
            </div>
            <div>
              <label className="overline block mb-2">Data evento</label>
              <input
                type="date"
                name="event_date"
                value={form.event_date}
                onChange={onChange}
                className="input-pw"
                data-testid="form-date"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Tipo di servizio</label>
              <select
                name="event_type"
                value={form.event_type}
                onChange={onChange}
                className="input-pw bg-transparent"
                data-testid="form-event-type"
              >
                <option value="" className="bg-[var(--pw-surface)]">Seleziona...</option>
                <option value="Pack Base — Cerimonia" className="bg-[var(--pw-surface)]">Pack Base — Cerimonia</option>
                <option value="Pack Tradizionale" className="bg-[var(--pw-surface)]">Pack Tradizionale</option>
                <option value="Pack Completo" className="bg-[var(--pw-surface)]">Pack Completo</option>
                <option value="Evento privato" className="bg-[var(--pw-surface)]">Evento privato / altro</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Oggetto</label>
              <input
                name="subject"
                value={form.subject}
                onChange={onChange}
                className="input-pw"
                placeholder="Matrimonio a Ferrara, 12 settembre"
                data-testid="form-subject"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Messaggio *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                className="input-pw"
                placeholder="Raccontaci la cerimonia che immagini..."
                data-testid="form-message"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-xs text-[var(--pw-text)]/50 font-light">
              Inviando questo modulo accetti di essere ricontattato per la richiesta.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="btn-pw btn-pw-solid disabled:opacity-60"
              data-testid="form-submit"
            >
              {loading ? "Invio in corso..." : "Invia Richiesta"}
              {!loading && <Send size={14} />}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
